// Admin resource registry — declarative field schemas drive the generic
// list + form pages. Adding a field here adds it to the admin UI; nothing
// else changes. Field types: text, textarea, select, checkbox, number, date,
// datetime, slug, list (string array), image (src+alt), blocks (rich body),
// group (nested object via dot-path children).

const STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'in_review', label: 'In Review' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

const DIVISIONS = [
  { value: 'operations', label: 'Operations' },
  { value: 'people', label: 'People' },
  { value: 'growth', label: 'Growth' },
  { value: 'launch', label: 'Launch' },
  { value: 'multi-service', label: 'Multi-Service' },
];

const TOPICS = [
  { value: 'operations', label: 'Operations' },
  { value: 'people', label: 'People' },
  { value: 'growth', label: 'Growth' },
  { value: 'launch', label: 'Launch' },
  { value: 'systems-technology', label: 'Systems & Technology' },
  { value: 'business-strategy', label: 'Business Strategy' },
  { value: 'leadership-management', label: 'Leadership & Management' },
];

const PURCHASE_MODES = [
  'direct_purchase', 'book_and_pay', 'pay_deposit', 'subscription_application',
  'submit_requirements', 'request_quote', 'book_consultation', 'contact_amden',
  'external_purchase', 'affiliate_link',
].map((v) => ({ value: v, label: v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) }));

const seoGroup = {
  key: 'seo', label: 'SEO', type: 'group', children: [
    { key: 'title', label: 'SEO Title', type: 'text' },
    { key: 'description', label: 'Meta Description', type: 'textarea' },
    { key: 'canonical', label: 'Canonical URL', type: 'text' },
    { key: 'ogTitle', label: 'Open Graph Title', type: 'text' },
    { key: 'ogDescription', label: 'Open Graph Description', type: 'textarea' },
    { key: 'ogImage', label: 'Open Graph Image URL', type: 'text' },
  ],
};

const workflowFields = [
  { key: 'status', label: 'Publication status', type: 'select', options: STATUSES },
  { key: 'scheduledFor', label: 'Scheduled for (Europe/Dublin)', type: 'datetime', showIf: (d) => d.status === 'scheduled' },
  { key: 'featured', label: 'Featured', type: 'checkbox' },
];

const TIMELINE_CATEGORIES = ['Foundation', 'Growth', 'People', 'Expansion', 'Transformation', 'Systems']
  .map((v) => ({ value: v, label: v }));

const TIMELINE_ICONS = [
  { value: 'flag', label: 'Flag — registration / foundation' },
  { value: 'building', label: 'Building — office / location' },
  { value: 'users', label: 'People — recruitment / team' },
  { value: 'handshake', label: 'Handshake — contract / partnership' },
  { value: 'trending', label: 'Growth chart — digital / growth' },
  { value: 'hardhat', label: 'Hard hat — fit-out / construction' },
  { value: 'globe', label: 'Globe — European / international reach' },
  { value: 'map', label: 'Map pin — geographical expansion' },
  { value: 'merge', label: 'Merge — business integration' },
  { value: 'sparkles', label: 'Sparkles — rebrand / launch' },
  { value: 'layers', label: 'Layers — sector expansion' },
  { value: 'compass', label: 'Compass — systems / platforms' },
  { value: 'cpu', label: 'Processor — technology product' },
];

export const RESOURCES = {
  pages: {
    label: 'Pages', singular: 'Page', idField: 'slug', labelField: 'name',
    icon: 'file', rolesWrite: ['owner', 'administrator', 'editor'],
    columns: ['name', 'route', 'status', 'updatedAt'],
    fields: [
      { key: 'name', label: 'Page name', type: 'text', required: true },
      { key: 'route', label: 'Route', type: 'text', required: true, help: 'Fixed public route, e.g. /services/operations' },
      { key: 'navLabel', label: 'Navigation label', type: 'text' },
      { key: 'hero.eyebrow', label: 'Hero eyebrow', type: 'text' },
      { key: 'hero.heading', label: 'Hero heading', type: 'text' },
      { key: 'hero.copy', label: 'Hero supporting copy', type: 'textarea' },
      { key: 'body', label: 'Page body', type: 'blocks', help: 'Structured content shown on standalone content pages (e.g. the legal pages). Main marketing pages manage their copy per section instead.' },
      { key: 'seo.indexable', label: 'Allow indexing', type: 'checkbox', default: true },
      seoGroup,
      ...workflowFields.filter((f) => f.key !== 'featured'),
    ],
  },
  articles: {
    label: 'Insights', singular: 'Article', idField: 'slug', labelField: 'title',
    icon: 'pen', rolesWrite: ['owner', 'administrator', 'editor'],
    columns: ['title', 'primaryTopic', 'status', 'publishedAt'],
    fields: [
      { key: 'title', label: 'Title', type: 'text', required: true },
      { key: 'slug', label: 'Slug', type: 'slug', source: 'title', help: 'Used in the URL /insights/[slug]. Changing a published slug creates a redirect automatically.' },
      { key: 'excerpt', label: 'Excerpt', type: 'textarea', required: true },
      { key: 'body', label: 'Article body', type: 'blocks' },
      { key: 'primaryTopic', label: 'Primary topic', type: 'select', options: TOPICS, required: true },
      { key: 'secondaryTopics', label: 'Additional topics', type: 'multiselect', options: TOPICS },
      { key: 'tags', label: 'Tags', type: 'list', help: 'Comma separated' },
      { key: 'keywords', label: 'Search keywords', type: 'list', help: 'Comma separated' },
      { key: 'authorId', label: 'Author', type: 'author', help: 'Leave empty to show the neutral "Amden Group" byline.' },
      { key: 'image', label: 'Featured image', type: 'image' },
      { key: 'publishedAt', label: 'Publication date', type: 'date' },
      { key: 'updatedAtContent', label: 'Genuine content update date', type: 'date', help: 'Only set when the article content materially changed.' },
      { key: 'recommended', label: 'Recommended reading', type: 'checkbox' },
      { key: 'relatedArticles', label: 'Related articles (slugs)', type: 'list' },
      { key: 'relatedServices', label: 'Related services', type: 'multiselect', options: DIVISIONS.filter((d) => d.value !== 'multi-service') },
      seoGroup,
      ...workflowFields,
    ],
  },
  listings: {
    label: 'Services & Ecosystem', singular: 'Listing', idField: 'slug', labelField: 'name',
    icon: 'grid', rolesWrite: ['owner', 'administrator', 'editor'],
    columns: ['name', 'itemType', 'serviceDivision', 'status', 'displayOrder'],
    filters: [
      { key: 'itemType', label: 'Type', options: ['amden_service', 'amden_system', 'service', 'system', 'package', 'partner', 'affiliate', 'training'] },
      { key: 'serviceDivision', label: 'Division', options: DIVISIONS.map((d) => d.value) },
    ],
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'slug', label: 'Slug', type: 'slug', source: 'name' },
      { key: 'itemType', label: 'Listing type', type: 'select', required: true, options: ['amden_service', 'amden_system', 'service', 'system', 'package', 'partner', 'affiliate', 'training'].map((v) => ({ value: v, label: v })) },
      { key: 'serviceDivision', label: 'Division', type: 'select', options: DIVISIONS },
      { key: 'relationship', label: 'Relationship', type: 'select', options: ['amden', 'partner', 'affiliate'].map((v) => ({ value: v, label: v })) },
      { key: 'tagline', label: 'Tagline', type: 'text' },
      { key: 'shortDescription', label: 'Short description', type: 'textarea', required: true },
      { key: 'description', label: 'Full description', type: 'textarea' },
      { key: 'problemSolved', label: 'Problem addressed', type: 'textarea' },
      { key: 'suitableFor', label: 'Suitable customer', type: 'textarea' },
      { key: 'features', label: 'Features / inclusions', type: 'list' },
      { key: 'areas', label: 'Areas', type: 'list' },
      { key: 'price', label: 'Price (EUR, number only when approved)', type: 'number' },
      { key: 'priceNote', label: 'Price display note', type: 'text', help: 'e.g. "From €495". Public price shows only when visibility is enabled and VAT treatment is set.' },
      { key: 'priceVisible', label: 'Show price publicly', type: 'checkbox', default: false },
      { key: 'vatTreatment', label: 'VAT treatment', type: 'select', options: [
        { value: '', label: 'Not set' },
        { value: 'inclusive', label: 'VAT included' },
        { value: 'exclusive', label: 'VAT excluded' },
        { value: 'exempt', label: 'VAT exempt' },
      ], showIf: (d) => !!d.priceVisible },
      { key: 'purchaseMode', label: 'Purchase mode', type: 'select', options: PURCHASE_MODES },
      { key: 'availability', label: 'Availability', type: 'select', options: ['available', 'limited', 'unavailable'].map((v) => ({ value: v, label: v })) },
      { key: 'href', label: 'Destination route', type: 'text' },
      { key: 'externalUrl', label: 'External URL', type: 'text' },
      { key: 'affiliateDisclosure', label: 'Affiliate disclosure', type: 'textarea', showIf: (d) => d.relationship === 'affiliate' || d.purchaseMode === 'affiliate_link' },
      { key: 'displayOrder', label: 'Display order', type: 'number' },
      { key: 'badge', label: 'Badge', type: 'select', options: [{ value: '', label: 'None' }, { value: 'new', label: 'New' }, { value: 'popular', label: 'Popular' }] },
      { key: 'image', label: 'Image', type: 'image' },
      { key: 'stripeProductId', label: 'Stripe product ID (future)', type: 'text' },
      { key: 'stripePriceId', label: 'Stripe price ID (future)', type: 'text' },
      // ── System product page (Amden Compass) — shown for system listings only ──
      {
        key: 'systemHero', path: 'systemPage', label: 'Product page — hero', type: 'group', children: [
          { key: 'strapline', label: 'Strapline', type: 'text', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'brandLine', label: 'Brand line (e.g. "Beyond Compliance")', type: 'text', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'heroEyebrow', label: 'Hero eyebrow', type: 'text', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'heroHeading', label: 'Hero heading (H1)', type: 'text', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'heroCopy', label: 'Hero supporting copy', type: 'textarea', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'capabilityLabels', label: 'Capability labels', type: 'list', showIf: (d) => d.itemType === 'amden_system' },
        ],
      },
      {
        key: 'systemShots', path: 'systemPage', label: 'Product page — screenshots', type: 'group', children: [
          { key: 'shots.dashboard', label: 'Desktop dashboard screenshot', type: 'image', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'shots.healthMobile', label: 'Business Health (mobile) screenshot', type: 'image', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'shots.kpiMobile', label: 'KPI Scorecard (mobile) screenshot', type: 'image', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'shots.askAmden', label: 'Ask Amden screenshot', type: 'image', showIf: (d) => d.itemType === 'amden_system' },
        ],
      },
      {
        key: 'systemContent', path: 'systemPage', label: 'Product page — content sections', type: 'group', children: [
          { key: 'problemCopy', label: 'Problem section intro', type: 'textarea', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'problems', label: 'Problem statements', type: 'items', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'rhythm', label: 'Operating rhythm steps', type: 'items', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'features', label: 'Platform capabilities', type: 'items', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'healthCopy', label: 'Business Health copy', type: 'textarea', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'healthAreas', label: 'Business Health areas', type: 'list', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'healthPoints', label: 'Business Health points', type: 'list', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'plansCopy', label: 'Plans & accountability copy', type: 'textarea', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'plansPoints', label: 'Plans & accountability points', type: 'list', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'kpiCopy', label: 'KPI Scorecards copy', type: 'textarea', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'kpiCategories', label: 'KPI categories', type: 'list', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'kpiPoints', label: 'KPI scorecard fields', type: 'list', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'coachingCopy', label: 'Coaching & Ask Amden copy', type: 'textarea', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'coachingPoints', label: 'Coaching points', type: 'list', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'askAmdenExamples', label: 'Ask Amden example questions', type: 'list', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'management', label: 'Issues/Resources/Reports items', type: 'items', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'devicesCopy', label: 'Devices section copy', type: 'textarea', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'devicesPoints', label: 'Devices points', type: 'list', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'audiences', label: 'Audiences', type: 'items', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'audiencesNote', label: 'Audiences supporting note', type: 'textarea', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'implementationCopy', label: 'Implementation copy', type: 'textarea', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'stages', label: 'Implementation stages', type: 'items', showIf: (d) => d.itemType === 'amden_system' },
        ],
      },
      {
        key: 'systemAccess', path: 'systemPage', label: 'Product page — pricing & access', type: 'group', children: [
          { key: 'pricingNote', label: 'Pricing note (shown until prices are approved)', type: 'textarea', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'pricingModels', label: 'Supported pricing models', type: 'list', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'monthlyPrice', label: 'Monthly price (EUR, only when approved)', type: 'number', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'annualPrice', label: 'Annual price (EUR, only when approved)', type: 'number', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'setupFee', label: 'Setup fee (EUR, only when approved)', type: 'number', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'userLimit', label: 'User limit', type: 'number', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'perEmployeePrice', label: 'Per-employee price (EUR, only when approved)', type: 'number', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'perSitePrice', label: 'Per-site price (EUR, only when approved)', type: 'number', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'consultancyFee', label: 'Consultancy-inclusive fee (EUR, only when approved)', type: 'number', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'includedEmployees', label: 'Included employee allowance', type: 'number', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'includedModules', label: 'Included modules', type: 'list', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'trialAvailable', label: 'Trial or demonstration access available', type: 'checkbox', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'planFeatures', label: 'Plan features', type: 'list', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'loginUrl', label: 'Existing-client login URL (verified live app only)', type: 'text', showIf: (d) => d.itemType === 'amden_system', help: 'Leave empty to hide the login link. Never a guessed or placeholder URL.' },
          { key: 'demoUrl', label: 'Demonstration URL (optional)', type: 'text', showIf: (d) => d.itemType === 'amden_system' },
        ],
      },
      {
        key: 'systemRelated', path: 'systemPage', label: 'Product page — related content', type: 'group', children: [
          { key: 'relatedCaseStudies', label: 'Related case studies (slugs)', type: 'list', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'relatedInsights', label: 'Related insights (slugs)', type: 'list', showIf: (d) => d.itemType === 'amden_system' },
          { key: 'relatedListings', label: 'Related systems/services (slugs)', type: 'list', showIf: (d) => d.itemType === 'amden_system' },
        ],
      },
      seoGroup,
      ...workflowFields,
    ],
  },
  'case-studies': {
    label: 'Case Studies', singular: 'Case Study', idField: 'slug', labelField: 'title',
    icon: 'award', rolesWrite: ['owner', 'administrator', 'editor'],
    columns: ['title', 'client', 'categories', 'status'],
    fields: [
      { key: 'client', label: 'Client name', type: 'text', required: true },
      { key: 'title', label: 'Project title', type: 'text', required: true },
      { key: 'slug', label: 'Slug', type: 'slug', source: 'title' },
      { key: 'industry', label: 'Industry (verified only)', type: 'text' },
      { key: 'categories', label: 'Service categories', type: 'multiselect', options: [...DIVISIONS] },
      { key: 'summary', label: 'Short summary', type: 'textarea', required: true },
      { key: 'overview', label: 'Project overview', type: 'textarea', toolbar: true, help: 'One or two short paragraphs shown in the public Overview section. Formatting: **bold**, *italic*, bullet lists (- item), [links](https://…).' },
      { key: 'challenge', label: 'Challenge', type: 'textarea', toolbar: true, help: 'Formatting: **bold**, *italic*, bullet lists (- item), [links](https://…).' },
      { key: 'solution', label: 'What Amden implemented', type: 'textarea', toolbar: true, help: 'Formatting: **bold**, *italic*, bullet lists (- item), [links](https://…).' },
      { key: 'outcomes', label: 'Verified outcomes', type: 'list' },
      { key: 'services', label: 'Services used', type: 'list' },
      { key: 'image', label: 'Featured image', type: 'image' },
      { key: 'gallery', label: 'Project gallery', type: 'gallery', help: 'Use Upload to add pictures straight from your device, or paste a /api/media/… URL — add alt text and an optional caption to each.' },
      { key: 'metrics', label: 'Metrics', type: 'items', keys: ['label', 'value'], placeholders: ['Label (e.g. Revenue growth)', 'Value (e.g. +32%)'], help: 'Verified figures only — publish quantitative claims only after client verification.' },
      { key: 'testimonial', label: 'Client testimonial', type: 'group', children: [
        { key: 'quote', label: 'Quote', type: 'textarea' },
        { key: 'name', label: 'Name', type: 'text' },
        { key: 'position', label: 'Position', type: 'text' },
        { key: 'company', label: 'Company', type: 'text' },
      ] },
      { key: 'related', label: 'Related case studies (slugs)', type: 'list' },
      { key: 'metricsVerified', label: 'Metrics verified with client', type: 'checkbox', help: 'Required before publishing case studies with quantitative metrics or testimonials.' },
      { key: 'order', label: 'Display order', type: 'number' },
      seoGroup,
      ...workflowFields,
    ],
    overview: true,
  },
  faqs: {
    label: 'FAQs', singular: 'FAQ', idField: 'faqId', labelField: 'question',
    icon: 'help', rolesWrite: ['owner', 'administrator', 'editor'],
    columns: ['question', 'assignedPage', 'status', 'order'],
    fields: [
      { key: 'question', label: 'Question', type: 'text', required: true },
      { key: 'answer', label: 'Answer', type: 'textarea', required: true },
      { key: 'assignedPage', label: 'Assigned page', type: 'select', options: ['contact', 'home', 'services', 'operations', 'people', 'growth', 'launch', 'ecosystem', 'insights', 'case-studies', 'about', 'compass', 'pulse', 'foresee'].map((v) => ({ value: v, label: v })) },
      { key: 'category', label: 'Category', type: 'text' },
      { key: 'order', label: 'Display order', type: 'number' },
      ...workflowFields.filter((f) => f.key !== 'featured'),
    ],
  },
  testimonials: {
    label: 'Testimonials', singular: 'Testimonial', idField: 'testimonialId', labelField: 'name',
    icon: 'quote', rolesWrite: ['owner', 'administrator', 'editor'],
    columns: ['name', 'company', 'status', 'approved'],
    fields: [
      { key: 'quote', label: 'Quotation (exact approved wording)', type: 'textarea', required: true },
      { key: 'name', label: 'Client name', type: 'text', required: true },
      { key: 'role', label: 'Role', type: 'text' },
      { key: 'company', label: 'Company', type: 'text' },
      { key: 'displayLocation', label: 'Display location', type: 'select', options: ['homepage', 'services', 'case-studies'].map((v) => ({ value: v, label: v })) },
      { key: 'approved', label: 'Wording and attribution approved by client', type: 'checkbox', help: 'Required before publication.' },
      { key: 'order', label: 'Display order', type: 'number' },
      ...workflowFields.filter((f) => f.key !== 'featured'),
    ],
  },
  'client-logos': {
    label: 'Client Logos', singular: 'Client Logo', idField: 'logoId', labelField: 'name',
    icon: 'image', rolesWrite: ['owner', 'administrator'],
    columns: ['name', 'status', 'order'],
    fields: [
      { key: 'name', label: 'Client name', type: 'text', required: true },
      { key: 'image', label: 'Logo image', type: 'image', help: 'Leave empty to keep the styled text wordmark.' },
      { key: 'href', label: 'Destination link (only when approved)', type: 'text' },
      { key: 'permissionConfirmed', label: 'Permission to display confirmed', type: 'checkbox' },
      { key: 'order', label: 'Display order', type: 'number' },
      ...workflowFields.filter((f) => f.key !== 'featured'),
    ],
  },
  authors: {
    label: 'Authors', singular: 'Author', idField: 'authorId', labelField: 'name',
    icon: 'user', rolesWrite: ['owner', 'administrator'],
    columns: ['name', 'role', 'status'],
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'role', label: 'Position', type: 'text', required: true },
      { key: 'bio', label: 'Short biography (verified only)', type: 'textarea' },
      { key: 'expertise', label: 'Areas of expertise', type: 'list' },
      { key: 'photo', label: 'Photograph', type: 'image' },
      { key: 'linkedIn', label: 'LinkedIn URL (verified only)', type: 'text' },
      { key: 'profileUrl', label: 'Profile route', type: 'text' },
      ...workflowFields.filter((f) => f.key !== 'featured'),
    ],
  },
  timeline: {
    label: 'Timeline', singular: 'Milestone', idField: 'milestoneId', labelField: 'title',
    icon: 'flag', rolesWrite: ['owner', 'administrator', 'editor'], noSeo: true,
    columns: ['title', 'displayDate', 'category', 'status'],
    filters: [
      { key: 'category', label: 'Category', options: TIMELINE_CATEGORIES.map((c) => c.value) },
    ],
    fields: [
      { key: 'title', label: 'Milestone title', type: 'text', required: true },
      { key: 'displayDate', label: 'Display date', type: 'text', required: true, help: 'As shown publicly, e.g. "May 2022".' },
      { key: 'sortDate', label: 'Chronological date', type: 'date', required: true, help: 'Published milestones are ordered by this stored date — not by display order.' },
      { key: 'year', label: 'Year', type: 'number', required: true },
      { key: 'month', label: 'Month (1–12)', type: 'number' },
      { key: 'exactDate', label: 'Exact date (only where known)', type: 'date' },
      { key: 'category', label: 'Category', type: 'select', options: TIMELINE_CATEGORIES, required: true },
      { key: 'icon', label: 'Icon', type: 'select', options: TIMELINE_ICONS, help: 'Shown when no approved image is set.' },
      { key: 'description', label: 'Description (verified wording only)', type: 'textarea', required: true },
      { key: 'image', label: 'Historic image or logo', type: 'image', help: 'Approved imagery only — never stock photos. Left empty, the icon is shown.' },
      { key: 'link', label: 'Destination link (optional internal route)', type: 'text' },
      { key: 'groupKey', label: 'Connected-event group (optional)', type: 'text', help: 'Milestones sharing a group render as one connected event, e.g. amden-transformation-2025.' },
      { key: 'displayOrder', label: 'Display order (same-month tie-breaker)', type: 'number' },
      ...workflowFields.map((f) => f.key === 'featured'
        ? { ...f, help: 'The featured milestone is selected when the timeline first loads.' }
        : f),
    ],
  },
  redirects: {
    label: 'Redirects', singular: 'Redirect', idField: 'redirectId', labelField: 'source',
    icon: 'route', rolesWrite: ['owner', 'administrator'],
    columns: ['source', 'destination', 'type', 'hitCount'],
    noWorkflow: true,
    fields: [
      { key: 'source', label: 'Source path (internal, e.g. /old-page)', type: 'text', required: true },
      { key: 'destination', label: 'Destination path (internal)', type: 'text', required: true },
      { key: 'type', label: 'Type', type: 'select', options: [{ value: '301', label: '301 Permanent' }, { value: '302', label: '302 Temporary' }] },
      { key: 'reason', label: 'Reason (internal)', type: 'text' },
      { key: 'active', label: 'Active', type: 'checkbox', default: true },
    ],
  },
};

// Sidebar navigation — role-filtered in the shell.
export const ADMIN_NAV = [
  { label: 'Dashboard', path: '/admin', icon: 'home', roles: ['owner', 'administrator', 'editor'] },
  { label: 'Analytics', path: '/admin/analytics', icon: 'chart', roles: ['owner', 'administrator', 'editor'] },
  { label: 'Pages', path: '/admin/content/pages', icon: 'file', roles: ['owner', 'administrator', 'editor'] },
  { label: 'Services', path: '/admin/content/listings?itemType=service', icon: 'briefcase', roles: ['owner', 'administrator', 'editor'] },
  { label: 'Systems', path: '/admin/content/listings?itemType=system', icon: 'cpu', roles: ['owner', 'administrator', 'editor'] },
  { label: 'Ecosystem', path: '/admin/content/listings', icon: 'grid', roles: ['owner', 'administrator', 'editor'] },
  { label: 'Case Studies', path: '/admin/content/case-studies', icon: 'award', roles: ['owner', 'administrator', 'editor'] },
  { label: 'Insights', path: '/admin/content/articles', icon: 'pen', roles: ['owner', 'administrator', 'editor'] },
  { label: 'Authors', path: '/admin/content/authors', icon: 'user', roles: ['owner', 'administrator'] },
  { label: 'FAQs', path: '/admin/content/faqs', icon: 'help', roles: ['owner', 'administrator', 'editor'] },
  { label: 'Testimonials', path: '/admin/content/testimonials', icon: 'quote', roles: ['owner', 'administrator', 'editor'] },
  { label: 'Client Logos', path: '/admin/content/client-logos', icon: 'image', roles: ['owner', 'administrator'] },
  { label: 'Timeline', path: '/admin/content/timeline', icon: 'flag', roles: ['owner', 'administrator', 'editor'] },
  { label: 'Media', path: '/admin/media', icon: 'folder', roles: ['owner', 'administrator', 'editor'] },
  { label: 'Bookings', path: '/admin/bookings', icon: 'calendar', roles: ['owner', 'administrator'] },
  { label: 'Availability', path: '/admin/availability', icon: 'clock', roles: ['owner', 'administrator'] },
  { label: 'Enquiries', path: '/admin/enquiries', icon: 'inbox', roles: ['owner', 'administrator'] },
  { label: 'Navigation', path: '/admin/navigation', icon: 'menu', roles: ['owner', 'administrator'] },
  { label: 'Redirects', path: '/admin/content/redirects', icon: 'route', roles: ['owner', 'administrator'] },
  { label: 'Site Settings', path: '/admin/settings', icon: 'settings', roles: ['owner', 'administrator'] },
  { label: 'Users', path: '/admin/users', icon: 'users', roles: ['owner'] },
  { label: 'Audit Log', path: '/admin/audit', icon: 'list', roles: ['owner', 'administrator'] },
];
